import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BadgeCheck,
  ChevronRight,
  Gift,
  Info,
  Layers,
  MessageCircle,
  PhoneIncoming,
  PhoneOutgoing,
  Shield,
  Send,
  Users,
} from 'lucide-react';
import {
  FaWhatsapp,
  FaFacebookF,
  FaInstagram,
  FaWeixin,
  FaGlobe,
  FaCommentSms,
  FaEnvelope,
} from 'react-icons/fa6';
import { SiLine } from 'react-icons/si';
import { fetchPlanLimitsFromAPI } from 'services/LimitsService';
import {
  CALCULATOR_STATIC_PLAN_LIMITS,
  INCLUDED_CONCURRENT_CALLS,
  NEED_TYPES,
  anyFixedPlanFitsUsage,
  formatCount,
  formatUsd,
  getUsageFlags,
  mergePlanLimits,
  recommendPricingPlan,
} from 'utils/pricingCostCalculator';
import './pricing-cost-calculator.css';

const NEED_OPTIONS = [
  {
    key: NEED_TYPES.CHAT,
    title: 'AI Chat',
    icon: MessageCircle,
    tone: 'blue',
  },
  {
    key: NEED_TYPES.INBOUND,
    title: 'Inbound Voice AI',
    icon: PhoneIncoming,
    tone: 'cyan',
  },
  {
    key: NEED_TYPES.OUTBOUND,
    title: 'Outbound Voice AI',
    icon: Send,
    tone: 'purple',
  },
  {
    key: NEED_TYPES.VOICE_CHAT,
    title: 'Voice + Chat',
    icon: Layers,
    tone: 'blend',
  },
];

const CHANNELS = [
  { key: 'whatsapp', label: 'WhatsApp', icon: FaWhatsapp, color: '#25D366' },
  { key: 'line', label: 'LINE', icon: SiLine, color: '#06C755' },
  { key: 'facebook', label: 'Facebook', icon: FaFacebookF, color: '#1877F2' },
  { key: 'instagram', label: 'Instagram', icon: FaInstagram, color: '#E4405F' },
  { key: 'wechat', label: 'WeChat', icon: FaWeixin, color: '#07C160' },
  { key: 'web', label: 'Website', icon: FaGlobe, color: '#2563EB' },
  { key: 'sms', label: 'SMS', icon: FaCommentSms, color: '#64748B' },
  { key: 'email', label: 'Email', icon: FaEnvelope, color: '#EA4335' },
];

const TRUST_BADGES = [
  { icon: Shield, label: 'Transparent Pricing' },
  { icon: BadgeCheck, label: 'No Hidden Costs' },
];

const SLIDER_CONFIG = {
  monthlyChats: { min: 0, max: 50000, step: 500, default: 2000 },
  inboundMinutes: { min: 0, max: 20000, step: 100, default: 400 },
  outboundMinutes: { min: 0, max: 20000, step: 100, default: 0 },
  concurrentCalls: { min: 1, max: 20, step: 1, default: 3 },
};

const buildSalesMailto = ({ email, phone, usageSummary, reasons }) => {
  const body = [
    'Hi VOICERAcx team,',
    '',
    'I need help with a plan that fits my requirements.',
    '',
    `Email: ${email}`,
    `Phone: ${phone}`,
    '',
    'Estimated usage:',
    usageSummary,
    '',
    ...(reasons?.length ? ['Notes:', ...reasons.map((r) => `- ${r}`), ''] : []),
    'Please contact me with a personalized quote.',
  ].join('\n');

  return `mailto:contact@voiceracx.ai?subject=${encodeURIComponent(
    'Custom plan request — cost calculator'
  )}&body=${encodeURIComponent(body)}`;
};

const SalesContactModal = ({
  open,
  onClose,
  reasons,
  usageSummary,
}) => {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setError('');
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();

    if (!trimmedEmail || !trimmedPhone) {
      setError('Please enter both email and phone number.');
      return;
    }

    window.location.href = buildSalesMailto({
      email: trimmedEmail,
      phone: trimmedPhone,
      usageSummary,
      reasons,
    });
    onClose();
  };

  return (
    <div className="pcc-modal" role="presentation" onClick={onClose}>
      <div
        className="pcc-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pcc-sales-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="pcc-modal__close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h3 id="pcc-sales-title" className="pcc-modal__title">
          Talk to our sales team
        </h3>
        <p className="pcc-modal__text">
          Your requirements need a personalized plan. Share your details and we will connect with
          you shortly.
        </p>
        {reasons?.length > 0 && (
          <ul className="pcc-modal__reasons">
            {reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        )}
        <form className="pcc-modal__form" onSubmit={handleSubmit}>
          <label className="pcc-modal__field">
            <span>Email address</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
            />
          </label>
          <label className="pcc-modal__field">
            <span>Phone number</span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 555 000 0000"
              required
            />
          </label>
          {error && <p className="pcc-modal__error">{error}</p>}
          <button type="submit" className="pcc-modal__submit">
            Contact Sales
          </button>
        </form>
      </div>
    </div>
  );
};

const sliderFill = (value, min, max) => {
  if (max <= min) return '0%';
  return `${Math.round(((value - min) / (max - min)) * 100)}%`;
};

const CalculatorHeroArt = () => (
  <svg
    className="pcc-hero-art"
    viewBox="0 0 120 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <ellipse cx="60" cy="88" rx="34" ry="6" fill="rgba(0,0,0,0.08)" />
    <rect x="28" y="18" width="64" height="58" rx="10" fill="#2563EB" />
    <rect x="34" y="24" width="52" height="16" rx="4" fill="#4F46E5" />
    <rect x="36" y="46" width="12" height="12" rx="3" fill="#93C5FD" />
    <rect x="52" y="46" width="12" height="12" rx="3" fill="#93C5FD" />
    <rect x="68" y="46" width="12" height="12" rx="3" fill="#93C5FD" />
    <rect x="36" y="62" width="12" height="12" rx="3" fill="#BFDBFE" />
    <rect x="52" y="62" width="12" height="12" rx="3" fill="#BFDBFE" />
    <rect x="68" y="62" width="12" height="12" rx="3" fill="#60A5FA" />
    <circle cx="94" cy="28" r="11" fill="#FBBF24" stroke="#fff" strokeWidth="2" />
    <text x="94" y="32" textAnchor="middle" fontSize="11" fontWeight="700" fill="#92400E">
      $
    </text>
    <rect x="8" y="34" width="18" height="14" rx="4" fill="#fff" stroke="#BFDBFE" strokeWidth="1.5" />
    <rect x="11" y="40" width="4" height="6" rx="1" fill="#2563EB" />
    <rect x="17" y="38" width="4" height="8" rx="1" fill="#60A5FA" />
    <circle cx="104" cy="58" r="10" fill="#fff" stroke="#BFDBFE" strokeWidth="1.5" />
    <path
      d="M99 58c0-2.8 2.2-5 5-5s5 2.2 5 5-2.2 5-5 5"
      stroke="#2563EB"
      strokeWidth="1.5"
      fill="none"
    />
  </svg>
);

const PricingCostCalculator = () => {
  const [planLimits, setPlanLimits] = useState(CALCULATOR_STATIC_PLAN_LIMITS);
  const [loading, setLoading] = useState(false);
  const [needType, setNeedType] = useState(NEED_TYPES.CHAT);
  const [monthlyChats, setMonthlyChats] = useState(SLIDER_CONFIG.monthlyChats.default);
  const [inboundMinutes, setInboundMinutes] = useState(SLIDER_CONFIG.inboundMinutes.default);
  const [outboundMinutes, setOutboundMinutes] = useState(SLIDER_CONFIG.outboundMinutes.default);
  const [concurrentCalls, setConcurrentCalls] = useState(SLIDER_CONFIG.concurrentCalls.default);
  const [channels, setChannels] = useState(['web']);
  const [salesModalOpen, setSalesModalOpen] = useState(false);

  const usageFlags = useMemo(() => getUsageFlags(needType), [needType]);
  const usesVoice = usageFlags.usesInbound || usageFlags.usesOutbound;

  const usage = useMemo(
    () => ({
      needType,
      monthlyChats: usageFlags.usesChat ? monthlyChats : 0,
      inboundMinutes: usageFlags.usesInbound ? inboundMinutes : 0,
      outboundMinutes: usageFlags.usesOutbound ? outboundMinutes : 0,
      channels: usageFlags.usesChat ? (channels.length ? channels : ['web']) : [],
    }),
    [needType, monthlyChats, inboundMinutes, outboundMinutes, channels, usageFlags]
  );

  const recommendation = useMemo(
    () => recommendPricingPlan(usage, planLimits, { concurrentCalls }),
    [usage, planLimits, concurrentCalls]
  );

  const needsSalesContact = recommendation.salesContact?.needsSales;
  const salesReasons = recommendation.salesContact?.reasons || [];
  const needsConcurrencySupport = usesVoice && concurrentCalls > INCLUDED_CONCURRENT_CALLS;
  const hidePriceEstimate =
    recommendation.hideEstimate ||
    (needsSalesContact &&
      !anyFixedPlanFitsUsage(usage, planLimits) &&
      usage.channels.some((channel) => channel !== 'web'));

  const usageSummary = useMemo(() => {
    const lines = [`Need type: ${needType}`];
    if (usage.monthlyChats > 0) lines.push(`Monthly chats: ${formatCount(usage.monthlyChats)}`);
    if (usage.inboundMinutes > 0) {
      lines.push(`Inbound minutes: ${formatCount(usage.inboundMinutes)}`);
    }
    if (usage.outboundMinutes > 0) {
      lines.push(`Outbound minutes: ${formatCount(usage.outboundMinutes)}`);
    }
    if (usesVoice) lines.push(`Concurrent calls: ${formatCount(concurrentCalls)}`);
    if (usage.channels.length > 0) lines.push(`Channels: ${usage.channels.join(', ')}`);
    return lines.join('\n');
  }, [usage, needType, concurrentCalls, usesVoice]);

  useEffect(() => {
    if (needsSalesContact) {
      setSalesModalOpen(true);
    }
  }, [needsSalesContact, salesReasons.join('|')]);

  const metrics = recommendation.metrics;

  useEffect(() => {
    let mounted = true;
    fetchPlanLimitsFromAPI()
      .then((data) => {
        if (mounted && data && typeof data === 'object') {
          setPlanLimits(mergePlanLimits(data));
        }
      })
      .catch(() => {
        if (mounted) setPlanLimits(CALCULATOR_STATIC_PLAN_LIMITS);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleNeedTypeChange = (key) => {
    setNeedType(key);
    if (key === NEED_TYPES.CHAT) {
      setInboundMinutes(0);
      setOutboundMinutes(0);
    } else if (key === NEED_TYPES.INBOUND) {
      setMonthlyChats(0);
      setOutboundMinutes(0);
    } else if (key === NEED_TYPES.OUTBOUND) {
      setMonthlyChats(0);
      setInboundMinutes(0);
    }
  };

  const toggleChannel = (key) => {
    setChannels((prev) =>
      prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]
    );
  };

  const renderSlider = (id, label, value, onChange, config, tone) => (
    <div className={`pcc-slider pcc-slider--${tone}`} key={id}>
      <div className="pcc-slider__head">
        <label htmlFor={id}>
          {label}
          <Info size={14} className="pcc-slider__info" aria-hidden="true" />
        </label>
        <input
          id={`${id}-input`}
          type="number"
          className="pcc-slider__value-input"
          min={config.min}
          max={config.max}
          step={config.step}
          value={value}
          onChange={(e) => {
            const next = Number(e.target.value);
            if (!Number.isFinite(next)) return;
            onChange(Math.min(config.max, Math.max(config.min, next)));
          }}
        />
      </div>
      <input
        id={id}
        type="range"
        className="pcc-slider__range"
        style={{ '--fill': sliderFill(value, config.min, config.max) }}
        min={config.min}
        max={config.max}
        step={config.step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <div className="pcc-slider__scale">
        <span>{formatCount(config.min)}</span>
        <span>{formatCount(config.max)}</span>
      </div>
    </div>
  );

  return (
    <div className="pcc">
      <div className="pcc-shell">
        <div className="pcc-topbar">
          <div className="pcc-trust">
            {TRUST_BADGES.map(({ icon: Icon, label }) => (
              <span key={label} className="pcc-trust__item">
                <Icon size={14} strokeWidth={2} />
                {label}
              </span>
            ))}
          </div>
        </div>

        <header className="pcc-hero">
          <h2 className="pcc-hero__title">
            Calculate Your <span className="pcc-gradient">AI Cost</span> in 30 Seconds
          </h2>
          <p className="pcc-hero__subtitle">
            Choose what you need, estimate your usage and see your monthly cost instantly.
          </p>
        </header>

        <div className="pcc-layout">
          <div className="pcc-main">
            <section className="pcc-step">
              <p className="pcc-step__label">Step 1 — What do you need?</p>
              <div className="pcc-need-grid">
                {NEED_OPTIONS.map(({ key, title, icon: Icon, tone }) => {
                  const active = needType === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`pcc-need-card pcc-need-card--${tone} ${active ? 'is-active' : ''}`}
                      onClick={() => handleNeedTypeChange(key)}
                    >
                      {active && (
                        <span className="pcc-need-card__check" aria-hidden="true">
                          <BadgeCheck size={16} strokeWidth={2.5} />
                        </span>
                      )}
                      <span className="pcc-need-card__icon">
                        <Icon size={22} strokeWidth={2} />
                      </span>
                      <span className="pcc-need-card__title">{title}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="pcc-step">
              <p className="pcc-step__label">Step 2 — Estimated Monthly Usage</p>

              {usageFlags.usesChat && (
                <div className="pcc-channels">
                  <p className="pcc-channels__label">Chat Channel</p>
                  <div className="pcc-channels__row">
                    {CHANNELS.map(({ key, label, icon: Icon, color }) => {
                      const active = channels.includes(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          className={`pcc-channel-icon ${active ? 'is-active' : ''}`}
                          title={label}
                          aria-pressed={active}
                          onClick={() => toggleChannel(key)}
                        >
                          <Icon size={18} style={{ color }} aria-hidden="true" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="pcc-sliders">
                {usageFlags.usesChat &&
                  renderSlider(
                    'monthly-chats',
                    'Monthly Chats',
                    monthlyChats,
                    setMonthlyChats,
                    SLIDER_CONFIG.monthlyChats,
                    'blue'
                  )}
                {usageFlags.usesInbound &&
                  renderSlider(
                    'inbound-minutes',
                    'Inbound Voice Minutes',
                    inboundMinutes,
                    setInboundMinutes,
                    SLIDER_CONFIG.inboundMinutes,
                    'cyan'
                  )}
                {usageFlags.usesOutbound &&
                  renderSlider(
                    'outbound-minutes',
                    'Outbound Voice Minutes',
                    outboundMinutes,
                    setOutboundMinutes,
                    SLIDER_CONFIG.outboundMinutes,
                    'purple'
                  )}
                {usesVoice &&
                  renderSlider(
                    'concurrent-calls',
                    'Concurrent Calls',
                    concurrentCalls,
                    setConcurrentCalls,
                    SLIDER_CONFIG.concurrentCalls,
                    'cyan'
                  )}
              </div>

              {needsConcurrencySupport && (
                <p className="pcc-concurrency-notice">
                  Monthly plans include up to {INCLUDED_CONCURRENT_CALLS} concurrent calls. For{' '}
                  {formatCount(concurrentCalls)} simultaneous calls,{' '}
                  <button
                    type="button"
                    className="pcc-inline-link"
                    onClick={() => setSalesModalOpen(true)}
                  >
                    contact sales
                  </button>{' '}
                  for a personalized plan and pricing.
                </p>
              )}

              {needsSalesContact && (
                <p className="pcc-sales-banner">
                  {salesReasons[0]}{' '}
                  <button
                    type="button"
                    className="pcc-inline-link"
                    onClick={() => setSalesModalOpen(true)}
                  >
                    Contact sales
                  </button>{' '}
                  for a personalized quote.
                </p>
              )}
            </section>

            <div className="pcc-promo">
              <span className="pcc-promo__icon">
                <Gift size={22} strokeWidth={2} />
              </span>
              <p>Start your journey with AI today!</p>
            </div>
          </div>

          <aside className="pcc-aside" aria-live="polite">
            <div className="pcc-aside__hero">
              <div className="pcc-aside__hero-copy">
                <p>
                  Check your <strong>estimated cost</strong> now
                </p>
              </div>
              <CalculatorHeroArt />
            </div>

            <div className="pcc-aside__body">
              {loading ? (
                <div className="pcc-skeleton" aria-hidden="true" />
              ) : (
                <>
                  <div className="pcc-plan">
                    <p className="pcc-plan__eyebrow">
                      Recommended Plan
                      <Info size={13} aria-hidden="true" />
                    </p>
                    <div className="pcc-plan__head">
                      <span className="pcc-plan__badge" aria-hidden="true">
                        <BadgeCheck size={28} strokeWidth={2} />
                      </span>
                      <div>
                        <h3 className="pcc-plan__name">{metrics.displayName}</h3>
                        <p className="pcc-plan__tagline">{metrics.subtitle}</p>
                        {recommendation.limitSummary && (
                          <p className="pcc-plan__limits">{recommendation.limitSummary}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="pcc-metrics">
                    {usageFlags.usesChat && (
                      <div className="pcc-metric">
                        <MessageCircle size={16} />
                        <span className="pcc-metric__label">Monthly Chats</span>
                        <strong>{formatCount(usage.monthlyChats)}</strong>
                      </div>
                    )}
                    {usageFlags.usesInbound && (
                      <div className="pcc-metric">
                        <PhoneIncoming size={16} />
                        <span className="pcc-metric__label">Inbound Minutes</span>
                        <strong>{formatCount(usage.inboundMinutes)}</strong>
                      </div>
                    )}
                    {usageFlags.usesOutbound && (
                      <div className="pcc-metric">
                        <PhoneOutgoing size={16} />
                        <span className="pcc-metric__label">Outbound Minutes</span>
                        <strong>{formatCount(usage.outboundMinutes)}</strong>
                      </div>
                    )}
                    {usesVoice && (
                      <div className="pcc-metric">
                        <Users size={16} />
                        <span className="pcc-metric__label">Concurrent Calls</span>
                        <strong>{formatCount(concurrentCalls)}</strong>
                      </div>
                    )}
                  </div>

                  {needsConcurrencySupport && (
                    <p className="pcc-concurrency-notice pcc-concurrency-notice--aside">
                      Need more than {INCLUDED_CONCURRENT_CALLS} concurrent calls?{' '}
                      <button
                        type="button"
                        className="pcc-inline-link"
                        onClick={() => setSalesModalOpen(true)}
                      >
                        Contact sales
                      </button>{' '}
                      for a personalized quote.
                    </p>
                  )}

                  {needsSalesContact && (
                    <p className="pcc-sales-banner pcc-sales-banner--aside">
                      This setup needs a custom plan.{' '}
                      <button
                        type="button"
                        className="pcc-inline-link"
                        onClick={() => setSalesModalOpen(true)}
                      >
                        Contact sales
                      </button>
                    </p>
                  )}

                  {recommendation.addonItems?.length > 0 && (
                    <ul className="pcc-addons">
                      {recommendation.addonItems.map((addon) => (
                        <li key={addon.key}>
                          <span>{addon.label}</span>
                          <strong>+{formatUsd(addon.price)}</strong>
                        </li>
                      ))}
                    </ul>
                  )}

                  {recommendation.note && (
                    <p className="pcc-plan-note">{recommendation.note}</p>
                  )}

                  <div className="pcc-price">
                    <div className="pcc-price__head">
                      <span>Estimated Monthly Cost</span>
                      <span className="pcc-price__usd">
                        All prices in USD
                        <Info size={12} aria-hidden="true" />
                      </span>
                    </div>
                    {recommendation.breakdown?.length > 0 ? (
                      <p className="pcc-price__breakdown">
                        {recommendation.breakdown.join(' · ')}
                      </p>
                    ) : (
                      !recommendation.isUsageBased &&
                      recommendation.basePlanCost > 0 && (
                        <p className="pcc-price__breakdown">
                          Plan {formatUsd(recommendation.basePlanCost)}
                          {recommendation.addonCost > 0 &&
                            ` + add-ons ${formatUsd(recommendation.addonCost)}`}
                        </p>
                      )
                    )}
                    {hidePriceEstimate ? (
                      <p className="pcc-price__value pcc-price__value--custom">
                        Custom quote
                        <span>contact sales</span>
                      </p>
                    ) : (
                      <p className="pcc-price__value">
                        {formatUsd(recommendation.monthlyCost)}
                        <span>/month</span>
                      </p>
                    )}
                  </div>
                </>
              )}

              {needsSalesContact ? (
                <button
                  type="button"
                  className="pcc-cta"
                  onClick={() => setSalesModalOpen(true)}
                >
                  Contact Sales
                  <ChevronRight size={20} strokeWidth={2.5} />
                </button>
              ) : (
                <Link to="/auth/register-1" className="pcc-cta">
                  Start Free Trial
                  <ChevronRight size={20} strokeWidth={2.5} />
                </Link>
              )}
            </div>
          </aside>
        </div>
      </div>

      <SalesContactModal
        open={salesModalOpen}
        onClose={() => setSalesModalOpen(false)}
        reasons={salesReasons}
        usageSummary={usageSummary}
      />
    </div>
  );
};

export default PricingCostCalculator;
